import React, { useEffect, useRef } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Fade,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TeamMember from "./TeamMember";

const teamData = [
  {
    id: 1,
    name: "Vikas Sharma",
    image: "/assets/team/Vikas-sharma.jpg",
    bio: "A seasoned telecom and fintech executive of technical and delivery background with 22+ years of experience across network solutions, enterprise messaging, wallet, and digital financial solutions. Proven expertise in building global telecom businesses.",
    linkedin: "https://www.linkedin.com/in/vikas-sharma-24188018",
    email: "vikas@protodiag.com",
  },
  {
    id: 2,
    name: "Sameer Gupta",
    image: "/assets/team/sameer-gupta.jpg",
    bio: "A tech visionary and solution architect, Sameer has led the design of high-TPS TRAI DLT solutions, secure SMS firewalls, and carrier-integrated platforms. He drives the R&D and product innovation roadmap.",
    linkedin: "https://www.linkedin.com/in/sameer-gupta-67b0932a7",
    email: "sameer@protodiag.com",
  },
  {
    id: 3,
    name: "Anmol Tyagi",
    image: "/assets/team/anmol.jpg",
    bio: "Anmol leads full-stack development at ProtoDiag, driving the architecture of secure, scalable, and high-performance APIs. He plays a key role in aligning technology with product vision, ensuring high performance and engineering excellence across all solutions.",
    linkedin: "https://www.linkedin.com/in/anmol-tyagi-0176801b2",
    email: "anmol.tyagi@protodiag.com",
  },

  // {
  //   id: 4,
  //   name: "Dr. Chanderkant",
  //   role: "Technical Support Operator",
  //   image: "/assets/team/profile.jpg",
  //   bio: "Dr. Agarwal brings 15+ years of clinical expertise to ensure ProtoDiag's solutions meet real-world healthcare needs and regulatory standards.",
  //   linkedin: "https://linkedin.com/in/priya-agarwal",
  //   email: "priya.agarwal@protodiag.com",
  // },
];

const TeamSection = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate");
        }
      },
      { threshold: 0.1 },
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: "#f7f9fc",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage:
            "radial-gradient(circle at 10% 20%, rgba(216, 241, 230, 0.46) 0%, rgba(255,255,255,0) 90%)",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={1000}>
          <Box>
            <Typography
              variant="h3"
              component="h2"
              align="center"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textFillColor: "transparent",
              }}
            >
              Our Leadership Team
            </Typography>

            <Divider
              sx={{
                width: "80px",
                mx: "auto",
                mb: 4,
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
              }}
            />
          </Box>
        </Fade>

        <Fade in={true} timeout={1500}>
          <Typography
            variant="h6"
            component="p"
            align="center"
            sx={{
              mb: 8,
              maxWidth: 800,
              mx: "auto",
              color: "text.secondary",
              lineHeight: 1.6,
            }}
          >
            Meet the leaders driving ProtoDiag’s mission to redefine digital
            communication. With deep expertise in telecom infrastructure,
            cloud-native platforms, and compliance-focused messaging systems,
            our leadership team is building the future of intelligent, secure,
            and scalable connectivity.
          </Typography>
        </Fade>

        <Grid container spacing={4} justifyContent="center">
          {teamData.map((member, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={member.id}
              sx={{
                opacity: 0,
                animation: "fadeInUp 0.6s ease-out forwards",
                animationDelay: `${index * 0.2}s`,
                "@keyframes fadeInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(40px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              <TeamMember {...member} />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 8,
            p: 4,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            color="primary.dark"
            gutterBottom
            fontWeight="medium"
          >
            Join Our Team
          </Typography>
          <Typography variant="body1">
            Interested in working with us? We're always looking for passionate
            individuals to join our mission. Visit our careers page to see
            current openings.
          </Typography>
        </Box>
      </Container>

      {/* Custom SVG decoration */}
      {isLargeScreen && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: "15%",
              left: "5%",
              width: "120px",
              height: "120px",
              opacity: 0.3,
              animation: "float 8s ease-in-out infinite",
              "@keyframes float": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(-20px)" },
              },
            }}
          >
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path
                fill={theme.palette.primary.light}
                d="M41.9,-70.8C54.2,-62.9,63.9,-50.6,71.5,-36.6C79.2,-22.7,84.7,-7,83.7,8.4C82.6,23.7,75,38.6,64.2,49.9C53.5,61.2,39.6,68.9,24.5,74.5C9.4,80,-6.9,83.5,-23.3,81.2C-39.8,78.8,-56.5,70.7,-68.7,57.9C-80.8,45.1,-88.4,27.7,-88.8,10.2C-89.1,-7.3,-82.3,-24.9,-72.3,-39.2C-62.4,-53.5,-49.4,-64.5,-35.6,-71.8C-21.7,-79.1,-7.1,-82.7,7,-89.6C21,-96.5,42,-95.8,53.3,-85.9C64.5,-76,66.1,-56.9,41.9,-70.8Z"
                transform="translate(100 100)"
              />
            </svg>
          </Box>
          <Box
            sx={{
              position: "absolute",
              bottom: "10%",
              right: "5%",
              width: "150px",
              height: "150px",
              opacity: 0.2,
              animation: "float 10s ease-in-out infinite",
              animationDelay: "1s",
            }}
          >
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path
                fill={theme.palette.secondary.light}
                d="M42.7,-67.1C56.8,-59.5,70.8,-50.3,79.7,-37.2C88.6,-24,92.3,-6.8,89.7,9.4C87.1,25.7,78.1,41.1,65.8,51.5C53.5,61.9,37.7,67.4,22.1,73.7C6.6,80,-8.8,87.1,-23.5,85.2C-38.3,83.3,-52.4,72.4,-64.2,59.2C-76,46,-85.5,30.5,-89.4,13.2C-93.3,-4.1,-91.7,-23.3,-83.6,-39.2C-75.5,-55.2,-61,-68,-45.3,-75.2C-29.7,-82.5,-12.8,-84.2,1.2,-86.1C15.2,-88,30.5,-90.1,42.7,-85.6C55,-81.2,64.3,-70.3,42.7,-67.1Z"
                transform="translate(100 100)"
              />
            </svg>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TeamSection;
